import React, { useState, useEffect, useRef } from 'react';
import { calculateForward } from './pensionCalculator';
import KakaoAdBanner from './KakaoAdBanner';
import KrwAmountInput from './KrwAmountInput';

function ForwardCalculator({ showInlineMobileAd }) {
  const [formData, setFormData] = useState({
    current_age: 30,
    retirement_age: 60,
    initial_principal: 0,
    monthly_payment: 1000000,
    annual_return_rate: 7.0,
    pension_yield_rate: 4.0,
    life_expectancy_age: 100,
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value) || 0,
    }));
  };

  const handleAmountChange = (name) => (amount) => {
    setFormData((prev) => ({
      ...prev,
      [name]: amount,
    }));
  };

  const formatNumber = (num) =>
    new Intl.NumberFormat('ko-KR').format(num);

  const MIN_MONTHLY_PAYMENT = 10000;

  const handleCalculate = (e) => {
    e.preventDefault();
    setError('');

    if (formData.monthly_payment == null || formData.monthly_payment < MIN_MONTHLY_PAYMENT) {
      setError(`월 납입액은 최소 ${formatNumber(MIN_MONTHLY_PAYMENT)}원 이상이어야 합니다.`);
      return;
    }

    setLoading(true);
    try {
      const data = calculateForward(formData);
      setResult(data);
    } catch (err) {
      setError('계산 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Chart.js
  useEffect(() => {
    if (!result || !chartRef.current) return;

    const initChart = () => {
      if (!window.Chart) return;

      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }

      const ctx = chartRef.current?.getContext('2d');
      if (!ctx) return;

      const labels = result.cash_flow_details.map((d) => `${d.age}세`);
      const accumulatedData = result.cash_flow_details.map((d) => d.accumulated_amount);
      const paidData = result.cash_flow_details.map((d) => d.paid_amount);
      const interestData = result.cash_flow_details.map((d) => d.interest_earned);

      chartInstanceRef.current = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label: '적립액',
              data: accumulatedData,
              borderColor: '#9a6338',
              backgroundColor: 'rgba(154, 99, 56, 0.12)',
              tension: 0.2,
              fill: true,
              borderWidth: 2,
            },
            {
              label: '납입액',
              data: paidData,
              borderColor: '#c8874a',
              backgroundColor: 'rgba(200, 135, 74, 0.08)',
              tension: 0.2,
              borderWidth: 2,
            },
            {
              label: '수익',
              data: interestData,
              borderColor: '#e6b17a',
              backgroundColor: 'rgba(230, 177, 122, 0.08)',
              tension: 0.2,
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: 'index' },
          plugins: {
            title: {
              display: true,
              text: '연금 적립 과정 시뮬레이션',
              color: '#3b2417',
              font: { size: isMobile ? 13 : 15, weight: '800', family: 'Pretendard, sans-serif' },
            },
            legend: {
              display: true,
              position: isMobile ? 'bottom' : 'top',
              labels: { font: { size: isMobile ? 11 : 13 }, boxWidth: isMobile ? 12 : 15, color: '#6d5849' },
            },
          },
          scales: {
            x: {
              ticks: { font: { size: isMobile ? 9 : 12 }, maxRotation: isMobile ? 45 : 0, color: '#9a8778' },
              grid: { color: 'rgba(234, 217, 199, 0.5)' },
            },
            y: {
              beginAtZero: true,
              ticks: {
                font: { size: isMobile ? 9 : 12 },
                color: '#9a8778',
                callback: (value) => {
                  const fmt = new Intl.NumberFormat('ko-KR', {
                    notation: isMobile ? 'compact' : 'standard',
                    compactDisplay: 'short',
                  });
                  return fmt.format(value) + '원';
                },
              },
              grid: { color: 'rgba(234, 217, 199, 0.5)' },
            },
          },
        },
      });
    };

    const timer = setTimeout(initChart, 200);
    return () => {
      clearTimeout(timer);
      try {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
      } catch (err) {
        // ignore
      }
    };
  }, [result, isMobile]);

  return (
    <div>
      {/* Page hero */}
      <div className="lb-page-hero">
        <div className="lb-container">
          <p className="lb-eyebrow">Forward Calculator</p>
          <h1 className="lb-title-lg">일반 계산기</h1>
          <p className="lb-body">월 저축액으로 노후 현금흐름을 계산합니다</p>
        </div>
      </div>

      {/* Main layout */}
      <section className="lb-section">
        <div className="lb-container">
          <div className="lb-calc-layout">

            {/* ── Input form ── */}
            <div>
              <div className="lb-card lb-card-padded">
                <h2 className="lb-title-md" style={{ marginBottom: '24px' }}>계산 조건 입력</h2>

                <form onSubmit={handleCalculate}>
                  <div className="lb-form-row">
                    <div className="lb-form-grid-2">
                      <div>
                        <label className="lb-label">현재 나이</label>
                        <input
                          type="number"
                          name="current_age"
                          value={formData.current_age}
                          onChange={handleInputChange}
                          className="lb-input"
                          min="20"
                          max="65"
                          required
                        />
                        <p className="lb-form-hint">만 나이</p>
                      </div>
                      <div>
                        <label className="lb-label">은퇴 나이</label>
                        <input
                          type="number"
                          name="retirement_age"
                          value={formData.retirement_age}
                          onChange={handleInputChange}
                          className="lb-input"
                          min="50"
                          max="80"
                          required
                        />
                        <p className="lb-form-hint">은퇴 예정 나이</p>
                      </div>
                    </div>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">초기 자금 (원)</label>
                    <KrwAmountInput
                      name="initial_principal"
                      value={formData.initial_principal}
                      onValueChange={handleAmountChange('initial_principal')}
                      className="lb-input"
                      step={1000000}
                    />
                    <p className="lb-form-hint">이미 보유한 자산 (없으면 0)</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">월 납입액 (원)</label>
                    <KrwAmountInput
                      name="monthly_payment"
                      value={formData.monthly_payment}
                      onValueChange={handleAmountChange('monthly_payment')}
                      className="lb-input"
                      step={10000}
                      required
                    />
                    <p className="lb-form-hint">매월 정기 납입할 금액</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">연 복리 수익률 (%)</label>
                    <input
                      type="number"
                      name="annual_return_rate"
                      value={formData.annual_return_rate}
                      onChange={handleInputChange}
                      className="lb-input"
                      min="0"
                      max="20"
                      step="0.1"
                      required
                    />
                    <p className="lb-form-hint">납입 기간 동안 예상 수익률</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">은퇴 후 포트폴리오 연 수익률 (%)</label>
                    <input
                      type="number"
                      name="pension_yield_rate"
                      value={formData.pension_yield_rate}
                      onChange={handleInputChange}
                      className="lb-input"
                      min="0"
                      max="10"
                      step="0.1"
                      required
                    />
                    <p className="lb-form-hint">원금 보존은 이자/배당만, 원금 소진은 원금과 수익을 함께 사용합니다</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">기대수명</label>
                    <input
                      type="number"
                      name="life_expectancy_age"
                      value={formData.life_expectancy_age}
                      onChange={handleInputChange}
                      className="lb-input"
                      min={Math.max(51, formData.retirement_age + 1)}
                      max="150"
                      step="1"
                      required
                    />
                    <p className="lb-form-hint">은퇴 후 원금 소진 수령액을 계산할 마지막 나이</p>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {[100, 120, 150].map((age) => (
                        <button
                          key={age}
                          type="button"
                          className="lb-btn lb-btn-outline"
                          style={{ padding: '8px 12px', width: 'auto' }}
                          onClick={() => setFormData((prev) => ({ ...prev, life_expectancy_age: age }))}
                        >
                          {age}세
                        </button>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="lb-alert lb-alert-error">
                      ⚠️ {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="lb-btn lb-btn-primary"
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    {loading ? (
                      <><span className="lb-spinner" />계산 중...</>
                    ) : (
                      '🧮 연금 계산하기'
                    )}
                  </button>
                </form>
              </div>
            </div>

            {showInlineMobileAd && (
              <KakaoAdBanner className="calc-result-mobile-ad" placement="mobile" />
            )}

            {/* ── Results ── */}
            <div>
              {!result && (
                <div className="lb-card lb-card-padded" style={{ textAlign: 'center', padding: '56px 28px' }}>
                  <p style={{ fontSize: '40px', marginBottom: '16px' }}>📊</p>
                  <p className="lb-subtitle" style={{ marginTop: 0 }}>계산 결과가 여기에 표시됩니다</p>
                  <p className="lb-card-desc">좌측 폼에서 조건을 입력하고 계산해보세요!</p>
                </div>
              )}

              {result && (
                <>
                  {/* Summary stat cards */}
                  <div className="lb-grid-3" style={{ marginBottom: '24px' }}>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">최종 적립액</div>
                      <div className="lb-stat-value">
                        {formatNumber(result.final_accumulated_amount)}
                        <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                      </div>
                    </div>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">원금 보존 월 수령액</div>
                      <div className="lb-stat-value lb-stat-value--accent">
                        {formatNumber(result.monthly_cash_flow)}
                        <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                      </div>
                    </div>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">원금 소진 월 수령액</div>
                      <div className="lb-stat-value">
                        {formatNumber(result.depletion_monthly_pension)}
                        <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="lb-card lb-card-padded" style={{ marginBottom: '24px' }}>
                    <h3 className="lb-title-md" style={{ marginBottom: '20px' }}>📈 적립 과정 시각화</h3>
                    <div className="calc-chart-wrap">
                      <canvas ref={chartRef} />
                    </div>
                  </div>

                  {/* Summary details */}
                  <div className="lb-card lb-card-padded" style={{ marginBottom: '24px' }}>
                    <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>💰 결과 요약</h3>
                    <div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">총 납입액</span>
                        <span className="lb-detail-value">{formatNumber(result.total_paid_amount)}원</span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">최종 적립액</span>
                        <span className="lb-detail-value">{formatNumber(result.final_accumulated_amount)}원</span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">예상 수익</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          +{formatNumber(result.final_accumulated_amount - result.total_paid_amount)}원
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 보존 월 수령액</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(result.monthly_cash_flow)}원/월
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 월 수령액</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(result.depletion_monthly_pension)}원/월
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 수령 기간</span>
                        <span className="lb-detail-value">
                          {result.depletion_start_age}세~{result.depletion_end_age}세
                          ({result.depletion_withdrawal_years}년)
                        </span>
                      </div>
                      <p className="lb-form-hint" style={{ marginTop: '14px' }}>
                        원금 소진 결과는 수익률이 일정하고 선택한 기대수명에 원금이 0원이 된다고 가정한 단순 계산입니다.
                      </p>
                    </div>
                  </div>

                  <div className="lb-card lb-card-padded" style={{ marginBottom: '24px' }}>
                    <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>📅 원금 소진 수령 계획</h3>
                    <div>
                      {result.depletion_details
                        .filter((_, index) => index === 0 || (index + 1) % 10 === 0 || index === result.depletion_details.length - 1)
                        .map((detail, index) => (
                          <div key={index} className="lb-detail-item">
                            <div>
                              <span className="lb-detail-label" style={{ fontWeight: '700', color: 'var(--color-brown-800)' }}>
                                {detail.age}세
                              </span>
                              <span style={{ color: 'var(--color-text-muted)', marginLeft: '6px', fontSize: '13px' }}>
                                ({detail.year}년차)
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div className="lb-detail-value">
                                {formatNumber(detail.remaining_balance)}원
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--color-brown-600)', marginTop: '2px' }}>
                                월 {formatNumber(detail.monthly_pension)}원 수령
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* DataTable */}
                  <div className="lb-card lb-card-padded">
                    <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>📋 연도별 적립 상세</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="lb-table" width="100%" cellSpacing="0" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th>연차</th>
                            <th>나이</th>
                            <th>적립액</th>
                            <th>납입액</th>
                            <th>수익</th>
                            <th>수익률</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.cash_flow_details.map((detail, index) => (
                            <tr key={index}>
                              <td>{detail.year}년</td>
                              <td>{detail.age}세</td>
                              <td style={{ fontWeight: '700', color: 'var(--color-brown-700)' }}>
                                {formatNumber(detail.accumulated_amount)}원
                              </td>
                              <td>{formatNumber(detail.paid_amount)}원</td>
                              <td style={{ color: 'var(--color-brown-600)' }}>
                                +{formatNumber(detail.interest_earned)}원
                              </td>
                              <td>
                                <span className="lb-badge">
                                  {((detail.interest_earned / detail.paid_amount) * 100).toFixed(1)}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default ForwardCalculator;
